import { type AiBuilderChatPlaceholder } from '../constants';
import { type AiBuilderQuestionState } from '../utils/aiBuilderWelcomePolicy';
import { type PdfPageRange } from '../utils/pdfPageRanges';
import { type AiBuilderTurnHints } from '../utils/types';

import { type AiBuilderQuestionSetState } from './aiBuilderQuestionSetState';

export const AI_BUILDER_COMPOSER_MAX_MESSAGE_LENGTH = 1000;

export type AiBuilderChatEntry = 'chat' | 'file' | 'youtube' | 'website';

export type AiBuilderChatEntrySource =
  | 'ai_buttons'
  | 'floating_ai_build_button'
  /** 문제 카드 툴박스의 `AI로 생성` 버튼 — 선택 문제 기준 후속 선택지를 먼저 띄운다. */
  | 'quiz_card';

export type OpenAiBuilderChatParams = {
  entry: AiBuilderChatEntry;
  source: AiBuilderChatEntrySource;
};

export type AiBuilderSelectedQuizContext = {
  id: string;
  number: number;
  question: string;
};

export type AiBuilderUsageState = 'normal' | 'low' | 'blocked';

export type AiBuilderUsageInfo = {
  state: AiBuilderUsageState;
  remaining: number;
  limit: number;
};

export type AiBuilderResponseFeedback = 'good' | 'bad' | null;

export type AiBuilderPdfSelectionSnapshot = {
  pageCount: number;
  pageRanges: PdfPageRange[];
};

export type AiBuilderMessageAttachment =
  | {
      type: 'file';
      /** 서버로 직렬화하지 않는 브라우저 세션 내 미리보기 원본. */
      file: File;
      label: string;
      extension?: string;
      pdfSelection?: AiBuilderPdfSelectionSnapshot;
    }
  | {
      type: 'url';
      label: string;
    };

export type AiBuilderSelectedFile =
  | {
      file: File;
      pageCount?: never;
      pageRanges?: never;
    }
  | {
      file: File;
      pageCount: number;
      pageRanges: PdfPageRange[];
    };

export type AiBuilderServerSourceRegistrationRequest =
  | {
      sourceType: 'file';
      file: File;
      fileName?: string;
      pageRanges?: PdfPageRange[];
    }
  | {
      sourceType: 'youtube' | 'website';
      url: string;
    };

export type AiBuilderProgressStepStatus =
  | 'done'
  | 'running'
  | 'pending'
  | 'stopped';

export type AiBuilderProgressStep = {
  id: string;
  label: string;
  status: AiBuilderProgressStepStatus;
};

export type AiBuilderJumpLink = {
  quizNumber: number;
  label: string;
};

export type AiBuilderQuestionRef = {
  questionId: string;
  quizNumber: number;
  label: string;
};

export type AiBuilderAction =
  | {
      id: string;
      label: string;
      type: 'prompt';
      prompt: string;
      emoji?: string;
    }
  | {
      id: string;
      label: string;
      type: 'undoLastAiAction';
      prompt: string;
    }
  | {
      id: string;
      label: string;
      type: 'closePanel';
    };

export type AiBuilderSubmitPromptOptions = {
  turnHints?: AiBuilderTurnHints;
};

export type AiBuilderUserMessageRetryRequest = {
  clientRequestId: string;
} & (
  | {
      type: 'message';
      prompt: string;
      attachment?: AiBuilderMessageAttachment;
      selectedQuizContext: AiBuilderSelectedQuizContext | null;
      sourceRefs: string[];
      turnHints?: AiBuilderSubmitPromptOptions['turnHints'];
    }
  | {
      type: 'confirm';
      token: string;
      decision: AiBuilderConfirmDecision;
    }
  | {
      type: 'undo';
    }
  | {
      type: 'questionSet';
    }
);

export type AiBuilderChoiceOption = {
  id: string;
  label: string;
  /**
   * 서버가 보낸 canonical 프로토콜 값 (label→value 계약).
   * missing_slot='quiz_types' 질문의 'ox'|'multiple'|'subjective'|'essay' 등 —
   * label 은 이 값을 locale 카피로 렌더한 표시 문자열이다.
   */
  value?: string;
  description?: string;
  /**
   * 표시 전용 이모지. 렌더에서만 label 앞에 붙이며, 제출 텍스트(label)에는
   * 포함되지 않아 서버 turn 계약·트래킹에 영향이 없다.
   */
  emoji?: string;
  confirm?: AiBuilderConfirmOptionMetadata;
};

export type AiBuilderConfirmDecision = 'approve' | 'cancel';

export type AiBuilderConfirmOptionStyle = 'destructive' | 'secondary';

export type AiBuilderConfirmOptionMetadata = {
  decision: AiBuilderConfirmDecision;
  style: AiBuilderConfirmOptionStyle;
};

export type AiBuilderConfirmChoiceMetadata = {
  token: string;
  actionDescription?: string;
};

export type AiBuilderMessage =
  | {
      id: string;
      kind: 'user';
      content: string;
      attachment?: AiBuilderMessageAttachment;
      status: 'sent' | 'sending' | 'failed';
      retry?: AiBuilderUserMessageRetryRequest;
    }
  | {
      id: string;
      kind: 'assistant';
      content: string;
      traceId?: string;
      feedback?: AiBuilderResponseFeedback;
      feedbackPending?: boolean;
      jumpLinks?: AiBuilderJumpLink[];
      actions?: AiBuilderAction[];
    }
  | {
      id: string;
      kind: 'progress';
      title: string;
      steps: AiBuilderProgressStep[];
      status: 'running' | 'complete' | 'stopped';
      /**
       * 진행 중(status: 'running') 카드에서 타이핑 효과로 흘려보내는 모델의
       * 사고 과정. 답변 본문이 시작되거나 턴이 끝나면 제거되는 ephemeral 필드로,
       * 히스토리에는 남지 않는다.
       */
      thinkingText?: string;
    }
  | {
      id: string;
      kind: 'choice';
      title: string;
      description?: string;
      missingSlot?: string;
      selectionMode: 'single' | 'multiple';
      options: AiBuilderChoiceOption[];
      selectedOptionIds: string[];
      submitted: boolean;
      confirm?: AiBuilderConfirmChoiceMetadata;
      traceId?: string;
      feedback?: AiBuilderResponseFeedback;
      feedbackPending?: boolean;
    }
  | {
      id: string;
      kind: 'confirm';
      title: string;
      token: string;
      approveLabel: string;
      cancelLabel: string;
      actionDescription?: string;
      questionRefs?: AiBuilderQuestionRef[];
      submitted: boolean;
      traceId?: string;
      feedback?: AiBuilderResponseFeedback;
      feedbackPending?: boolean;
    }
  | {
      id: string;
      kind: 'system';
      tone: 'notice' | 'error';
      content: string;
      /** 서버 error event 의 code — turn_error 분석 이벤트의 failure_reason 재료. */
      errorCode?: string;
    };

export type AiBuilderProgressMessage = Extract<
  AiBuilderMessage,
  { kind: 'progress' }
>;

export type AiBuilderProgressMessageStatus = AiBuilderProgressMessage['status'];

export type AiBuilderPendingQuestionSetOption = {
  optionId: string;
  label: string;
  description: string | null;
  value: string | null;
};

export type AiBuilderPendingQuestionSetQuestion = {
  questionId: string;
  question: string;
  context: string;
  options: AiBuilderPendingQuestionSetOption[];
  selectionMode: 'single' | 'multiple';
  allowDirectInput: boolean;
  missingSlot: string;
};

export type AiBuilderPendingQuestionSet =
  AiBuilderQuestionSetState<AiBuilderPendingQuestionSetQuestion> & {
    /** interaction 진입 직전 일반 composer 입력. 질문별 draft와 섞지 않는다. */
    composerDraftSnapshot: string;
  };

export type AiBuilderSetPendingQuestionSetEvent = {
  type: 'setPendingQuestionSet';
  interactionId: string;
  questions: AiBuilderPendingQuestionSetQuestion[];
};

export type AiBuilderChatState = {
  panelOpen: boolean;
  conversationEntryIntent: AiBuilderChatEntry | null;
  conversationQuestionState: AiBuilderQuestionState | null;
  draft: string;
  placeholder: AiBuilderChatPlaceholder;
  selectedFile: AiBuilderSelectedFile | null;
  selectedQuizContext: AiBuilderSelectedQuizContext | null;
  sessionId: string | null;
  usage: AiBuilderUsageInfo | null;
  sourceRefs: string[];
  messages: AiBuilderMessage[];
  /** 초기 상태 호환을 위해 optional이며, 수신 뒤에는 독립 question-set state다. */
  pendingQuestionSet: AiBuilderPendingQuestionSet | null;
  isGenerating: boolean;
  turnInProgress: boolean;
};

export type AiBuilderChatActions = {
  openFromEntry: (params: OpenAiBuilderChatParams) => void;
  closePanel: () => void;
  setDraft: (draft: string) => void;
  setSelectedFile: (file: AiBuilderSelectedFile | null) => void;
  restartConversation: () => void;
  submitDraft: () => void;
  submitPrompt: (
    prompt: string,
    options?: AiBuilderSubmitPromptOptions,
  ) => void;
  cancelGeneration: () => void;
  resetServerSession: () => void;
  registerServerSource: (
    request: AiBuilderServerSourceRegistrationRequest,
  ) => void;
  selectChoiceOption: (messageId: string, optionId: string) => void;
  submitChoiceMessage: (messageId: string, responseText?: string) => void;
  submitConfirmMessage: (
    messageId: string,
    decision: AiBuilderConfirmDecision,
  ) => void;
  submitAssistantAction: (messageId: string, action: AiBuilderAction) => void;
  retryUserMessage: (messageId: string) => void;
  requestConfirmActionAttention: (messageId: string) => void;
  focusQuizByNumber: (quizNumber: number) => void;
  setResponseFeedback: (
    messageId: string,
    feedback: AiBuilderResponseFeedback,
  ) => void;
  clearSelectedQuizContext: () => void;
};
